import { Blocks } from 'lucide-react';
import { ChangeEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SelectablePill } from './SelectablePill';
import { APP_OTHER_API_ID, APPS } from './onboardingOptions';
import { useGettingStartedOnboarding } from './useGettingStartedOnboarding';
import { MCP_CATALOG_APPROX_COUNT } from '@/Domain/components/Action/RemoteMCPAction/mcpCatalog';
import { Field, FieldDescription, FieldLabel } from '@/Domain/components/Shared/Field/Field';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { looksLikeChannelQuery } from '@/Domain/helper/channelQuery';
import { looksLikeSecret } from '@/Domain/helper/secretShapes';

const OTHER_APP_INPUT_ID = 'gs-external-apps-other-input';
const SECRET_WARNING_ID = 'gs-external-apps-secret-warning';
const CHANNEL_HINT_ID = 'gs-external-apps-channel-hint';
const SAVE_ERROR_ID = 'gs-external-apps-save-error';

export const ExternalAppsStep = () => {
  const { t } = useTranslation('getting_started');
  const {
    externalAppsSelection,
    toggleExternalApp,
    externalAppsOtherText,
    setExternalAppsOtherText,
    externalAppsOtherTextSaveFailed,
  } = useGettingStartedOnboarding();
  const otherApiSelected = externalAppsSelection.has(APP_OTHER_API_ID);
  const secretTyped = looksLikeSecret(externalAppsOtherText);
  // The guard mirrors the API's, so a refusal here should be unreachable — but the write is
  // debounced and silent, and "typed it, it stayed on screen" must never be the only evidence a
  // user gets that it was stored. Ranked above the channel hint: it is about this value's fate.
  const saveFailed = !secretTyped && externalAppsOtherTextSaveFailed;
  const channelTyped = !secretTyped && !saveFailed && looksLikeChannelQuery(externalAppsOtherText);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2" data-testid="gs-external-apps-pills">
        {APPS.map((app) => (
          <SelectablePill
            key={app.id}
            label={app.labelKey ? t(app.labelKey) : app.label}
            icon={app.icon}
            iconColor={app.iconColor}
            selected={externalAppsSelection.has(app.id)}
            onToggle={() => toggleExternalApp(app.id)}
            testId={`gs-external-apps-pill-${app.id}`}
          />
        ))}
      </div>
      {/* The pills are a shortlist, not the offer: the catalog behind the browse pill carries
          MCP_CATALOG_APPROX_COUNT+ servers, and an API Action reaches anything else. Without this
          line the step reads as "these or nothing", which is the opposite of true. This sentence is
          the one place the catalog size is quoted — on a pill the same number would sit on a
          checkbox that browses nothing until onboarding ends, and would read as an exact count next
          to the floored one here. Deliberately the muted helper-line vocabulary used elsewhere in
          onboarding — it points at the last two pills, it does not compete with them. */}
      <p
        className="flex items-start gap-1.5 px-1 text-xs text-muted-foreground"
        data-testid="gs-external-apps-catalog-hint"
      >
        <Blocks className="size-3 shrink-0 translate-y-[0.15rem]" aria-hidden="true" />
        <span className="min-w-0">
          <Trans
            i18nKey="getting_started:external_apps_step.catalog_hint"
            values={{ count: MCP_CATALOG_APPROX_COUNT }}
            components={{ count: <span className="font-medium text-foreground" /> }}
          />
        </span>
      </p>
      {otherApiSelected && (
        <Field data-invalid={secretTyped || saveFailed} className="gap-1.5">
          <FieldLabel htmlFor={OTHER_APP_INPUT_ID} className="sr-only">
            {t('external_apps_step.other_app_label')}
          </FieldLabel>
          <Input
            id={OTHER_APP_INPUT_ID}
            type="text"
            value={externalAppsOtherText}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setExternalAppsOtherText(e.target.value)
            }
            placeholder={t('external_apps_step.other_api_placeholder')}
            maxLength={200}
            autoComplete="off"
            error={secretTyped || saveFailed}
            aria-invalid={secretTyped || saveFailed}
            aria-describedby={
              secretTyped
                ? SECRET_WARNING_ID
                : saveFailed
                  ? SAVE_ERROR_ID
                  : channelTyped
                    ? CHANNEL_HINT_ID
                    : undefined
            }
            data-testid={OTHER_APP_INPUT_ID}
          />
          <div role="status" aria-live="polite" aria-atomic="true">
            {secretTyped && (
              <FieldDescription
                id={SECRET_WARNING_ID}
                className="pb-0 text-warning-foreground"
                data-testid={SECRET_WARNING_ID}
              >
                {t('external_apps_step.secret_warning')}
              </FieldDescription>
            )}
            {saveFailed && (
              <FieldDescription
                id={SAVE_ERROR_ID}
                className="pb-0 text-warning-foreground"
                data-testid={SAVE_ERROR_ID}
              >
                {t('external_apps_step.save_error')}
              </FieldDescription>
            )}
            {channelTyped && (
              <FieldDescription id={CHANNEL_HINT_ID} className="pb-0" data-testid={CHANNEL_HINT_ID}>
                {t('external_apps_step.channel_hint')}
              </FieldDescription>
            )}
          </div>
        </Field>
      )}
    </div>
  );
};
